import React from 'react'
import Proptypes from 'react-proptypes'
import * as d3 from 'd3'
import styled from 'styled-components'
import LabelNames from './LabelNames'
import Legend from './Legend'
import Slices from './Slices'
import { connect } from 'react-redux'
import * as selectors from 'selectors'
import { highlightCurrencies } from 'actions/dashboard'
import { some } from 'lodash'
import compose from 'recompose/compose'

const Container = styled.div`
  svg {
    filter: drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.25));
  }
`

const merge = funds =>
  funds.reduce(
    (sum, val) => ({
      ...sum,
      value: sum.value + val.value,
      currencies: [].concat(sum.currencies, [val.currency]),
    }),
    {
      value: 0,
      name: 'Others',
      currencies: [],
    }
  )

class DonutChart extends React.Component {
  state = {
    current: 0,
    reverse: 0,
    sideSize: 0,
  }

  transitionDelay = 100

  interpolation = d3.interpolate(0, 10)

  onAnimationFrame = time => {
    if (!this.start) {
      this.start = time
    }
    const prog = (time - this.start) / this.transitionDelay
    this.setState({
      current: this.interpolation(prog),
      reverse: 10 - this.interpolation(prog),
    })
    if (prog < 1) {
      window.requestAnimationFrame(this.onAnimationFrame)
    }
  }

  componentDidUpdate = previous => {
    if (this.props.highlightedCurrencies !== previous.highlightedCurrencies) {
      this.start = null
      window.requestAnimationFrame(this.onAnimationFrame)
    }
  }

  initDonut = () => {
    const chartWidth = 700
    const chartHeight = 700
    const { funds } = this.props
    this.radius = Math.min(chartWidth - 100, chartHeight - 100) / 2
    const floatFormat = d3.format('.4r')
    const variable = 'value'
    const cornerRadius = 5 // sets how rounded the corners are on each slice
    const padAngle = 0.025 // effectively dictates the gap between slices
    const pie = d3
      .pie()
      .value(d => floatFormat(d[variable]))
      .sort(null)
    this.arc = d3
      .arc()
      // the radius is bigger when the currency is selected
      .outerRadius(
        d =>
          this.radius * 0.75 +
          // a slice can have either a currency of currencies (OTHER)
          ((d.data.currencies
          ? some(
              d.data.currencies,
              c => this.props.highlightedCurrencies.indexOf(c) > -1
            )
          : this.props.highlightedCurrencies.indexOf(d.data.currency) > -1)
            ? this.state.current
            : 0)
      )
      .innerRadius(this.radius * 0.6)
      .cornerRadius(cornerRadius)
      .padAngle(padAngle)
    this.outerArc = d3
      .arc()
      .outerRadius(this.radius)
      .innerRadius(this.radius)
    this.fundsClustered =
      funds.length <= 5
        ? funds
        : [].concat(
            funds.slice(0, 4),
            // merge the 5th and so on...
            merge(funds.slice(4))
          )
    this.slices = pie(this.fundsClustered)
  }

  onSliceMouseEnter = s => {
    this.props.highlightCurrencies(s.data.currencies || [s.data.currency])
  }

  onSliceMouseOut = s => {
    this.props.highlightCurrencies([])
  }

  resize = () => {
    this.setState(
      {
        sideSize: 0,
      },
      () =>
        this.setState({
          sideSize: Math.min(
            this.element.clientHeight,
            this.element.clientWidth
          ),
        })
    )
  }

  componentDidMount = () => {
    this.resize()
    window.addEventListener('resize', this.resize)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize)
  }

  render() {
    this.initDonut()
    const sharedProps = {
      mainCurrency: this.props.mainCurrency,
      category: 'currency',
      colour: this.props.colors,
      radius: this.radius,
      outerArc: this.outerArc,
      arc: this.arc,
      slices: this.slices,
    }

    return (
      <Container
        innerRef={ref => (this.element = ref)}
        className={this.props.className}
      >
        <svg
          style={{
            width: this.state.sideSize,
            height: this.state.sideSize,
          }}
          preserveAspectRatio="xMidYMid meet"
          viewBox={`0 0 700 700`}
        >
          <g transform={`translate(${700 / 2}, ${700 / 2})`}>
            <Slices
              {...sharedProps}
              onMouseEnter={this.onSliceMouseEnter}
              onMouseOut={this.onSliceMouseOut}
            />
            <LabelNames {...sharedProps} />
            <Legend data={this.fundsClustered} {...sharedProps} />
          </g>
        </svg>
      </Container>
    )
  }
}

DonutChart.propTypes = {
  funds: Proptypes.array.isRequired,
}
const mapStateToProps = state => ({
  highlightedCurrencies: selectors.highlightedCurrencies(state),
})
const mapDispatchToProps = dispatch => ({
  highlightCurrencies: c => dispatch(highlightCurrencies(c)),
})
export default compose(connect(mapStateToProps, mapDispatchToProps))(DonutChart)



// WEBPACK FOOTER //
// ./src/components/Donut/index.js